import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Navbar from "../Navbar";
import {
  Container,
  Card,
  Table,
  Button,
  Badge,
  Row,
  Col,
  Spinner,
  Modal,
  ListGroup,
} from "react-bootstrap";
import { API_ENDPOINTS } from '../config/api';
import PageTitle from '../components/PageTitle';
import UserAvatar from "../components/UserAvatar";

const TemplateEvaluationsPage = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [template, setTemplate] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [selectedRecurringEvaluation, setSelectedRecurringEvaluation] = useState(null);
  const [unlockingEvaluation, setUnlockingEvaluation] = useState(null);
  
  // Get due date from URL parameters
  const dueDateParam = searchParams.get('dueDate');
  const selectedDueDate = dueDateParam ? new Date(dueDateParam).toDateString() : null;

  useEffect(() => {
    const fetchTemplateEvaluations = async () => {
      try {
        const token = localStorage.getItem("token");
        let fetchedTemplate = null;
        
        // Fetch template details
        const templateRes = await fetch(
          `${API_ENDPOINTS.DIRECTOR}/evaluation-templates/${templateId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (templateRes.ok) {
          const templateData = await templateRes.json();
          fetchedTemplate = templateData?.template || null;
          setTemplate(templateData.template);
        }

        // Fetch all evaluations for this template
        const evaluationsRes = await fetch(
          `${API_ENDPOINTS.DIRECTOR}/evaluations-by-template/${templateId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (evaluationsRes.ok) {
          const evaluationsData = await evaluationsRes.json();
          setEvaluations(evaluationsData);

          // Fallback for cases where template details are unavailable through the template endpoint
          if (!fetchedTemplate && evaluationsData.length > 0) {
            setTemplate({
              title: evaluationsData[0].templateTitle || evaluationsData[0].title || "Template Evaluations",
              evaluationType: evaluationsData[0].evaluationType || null,
            });
          }
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching template evaluations:", error);
        setLoading(false);
      }
    };

    fetchTemplateEvaluations();
  }, [templateId]);

  // Group evaluations by assignee (stored in resident fields), optionally filtered by due date
  const groupEvaluationsByResident = (evaluations) => {
    // Filter evaluations by due date if specified
    let filteredEvaluations = evaluations;
    if (selectedDueDate) {
      filteredEvaluations = evaluations.filter(evaluation =>
        evaluation.dueDate && new Date(evaluation.dueDate).toDateString() === selectedDueDate
      );
      // Avoid dead-end blank state caused by timezone/date conversion drift in URL params
      if (filteredEvaluations.length === 0) {
        filteredEvaluations = evaluations;
      }
    }

    const grouped = filteredEvaluations.reduce((acc, evaluation) => {
      const residentId = evaluation.residentId;
      const residentName = evaluation.resident 
        ? `${evaluation.resident.firstName} ${evaluation.resident.lastName}`
        : `Resident ${residentId}`;

      if (!acc[residentId]) {
        acc[residentId] = {
          residentId,
          residentName,
          resident: evaluation.resident,
          evaluations: [],
        };
      }

      acc[residentId].evaluations.push(evaluation);
      return acc;
    }, {});

    // Sort evaluations within each resident group by due date
    Object.values(grouped).forEach(group => {
      group.evaluations.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
    });

    return Object.values(grouped);
  };

  const handleUnlockEvaluation = async (evaluationId) => {
    setUnlockingEvaluation(evaluationId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.DIRECTOR}/unlock-evaluation/${evaluationId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Evaluation unlocked successfully! ${result.message}`);
        // Refresh the page to show updated status
        window.location.reload();
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to unlock evaluation');
      }
    } catch (error) {
      console.error('Error unlocking evaluation:', error);
      alert('Failed to unlock evaluation');
    } finally {
      setUnlockingEvaluation(null);
    }
  };

  const RecurringEvaluationsModal = ({ show, handleClose, evaluations, residentName }) => {
    const userRole = localStorage.getItem('role');
    const isDirector = userRole === 'director';

    if (!evaluations || evaluations.length === 0) return null;

    return (
      <Modal show={show} onHide={handleClose} size="lg">
        <Modal.Header closeButton className="border-0 px-4">
          <div className="d-flex flex-column">
          <h6 className="fw-semibold mb-0">Recurring Evaluations for <span className="text-primary">{residentName}</span>
          </h6>
          <div className="text-hl"></div>
          </div>
        </Modal.Header>
        <Modal.Body className="px-4 pb-0">
            {evaluations.map((evaluation) => {
              const isLocked = evaluation.startTime && new Date(evaluation.startTime) > new Date();
              const isCompleted = evaluation.status === "completed";
              
              return (
                <div
                  key={evaluation.id}
                  className="border-alt-light custom-hover-bg-alt rounded-3 p-3 mb-2 d-flex flex-column"
                >
                  <div className="fw-semibold mb-0">
                    {evaluation.term === "mid" || evaluation.title === "Mid Evaluation"
                      ? "Mid Evaluation"
                      : evaluation.term === "final" || evaluation.title === "Final Evaluation"
                        ? "End of Term Evaluation"
                        : evaluation.title || "Evaluation"}
                  </div>
                  <div className="d-flex align-items-center justify-content-between flex-wrap">
                    <div className="d-flex flex-column flex-md-row gap-3 align-items-md-center">
                      <div className="text-muted small">
                        <span className="fw-semibold">Due:</span> {evaluation.dueDate ? new Date(evaluation.dueDate).toLocaleDateString() : "Not set"}
                      </div>
                      <div className="text-muted small">
                        <span className="fw-semibold">Start:</span>{" "}
                        {evaluation.startTime
                          ? `${new Date(evaluation.startTime).toLocaleDateString()} ${new Date(evaluation.startTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }).toUpperCase()}`
                          : "-"}
                      </div>
                      <div className="text-muted small">
                        <span className="fw-semibold">End:</span>{" "}
                        {evaluation.endTime
                          ? `${new Date(evaluation.endTime).toLocaleDateString()} ${new Date(evaluation.endTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }).toUpperCase()}`
                          : "-"}
                      </div>
                 
                      <div>
                        <span className="rounded px-2 py-1 bg-light d-inline-flex align-items-center">
                          <i className={`bi bi-circle-fill align-middle me-2 ${isCompleted ? 'text-success' : isLocked ? 'text-secondary' : 'text-warning'}`} style={{fontSize: '0.4rem'}}></i>
                          {isCompleted ? "Completed" : isLocked ? "Locked" : "Pending"}
                        </span>
                      </div>
                    </div>
                    <div className="d-flex gap-2 mt-2 mt-md-0">
                      {isDirector && isLocked && !isCompleted && (
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => handleUnlockEvaluation(evaluation.id)}
                          disabled={unlockingEvaluation === evaluation.id}
                        >
                          {unlockingEvaluation === evaluation.id ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                              Unlocking...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-unlock me-1"></i>
                              Unlock
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                          handleClose();
                          if (isCompleted) {
                            navigate(`/evaluations/response/${evaluation.responseId}`);
                          } else {
                            navigate(`/evaluations/${evaluation.id}/complete`);
                          }
                        }}
                        disabled={isLocked}
                      >
                        {isCompleted 
                          ? "View" 
                          : isLocked
                            ? `Available ${new Date(evaluation.startTime).toLocaleDateString()}`
                            : "Complete"}
                      </Button>
                    </div>
                  </div>
                </div>
           
              );
            })}
        </Modal.Body>
        <Modal.Footer className="border-0 px-4">
          <Button variant="secondary" size="sm" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  if (loading) {
    return (
      <div>
        <PageTitle title="Loading..." />
        <Navbar pageTitle="Template Evaluations" />
        <Container className="mt-4">
          <div className="text-center">
            <Spinner animation="border" />
            <p className="mt-2">Loading evaluations...</p>
          </div>
        </Container>
      </div>
    );
  }

  const groupedEvaluations = groupEvaluationsByResident(evaluations);
  const resolvedEvaluationType = (
    template?.evaluationType ||
    template?.type ||
    evaluations?.[0]?.evaluationType ||
    ""
  ).toLowerCase();

  const evaluationTypeMeta =
    resolvedEvaluationType === "performance"
      ? { label: "Performance", className: "text-primary" }
      : resolvedEvaluationType === "feedback"
        ? { label: "Feedback", className: "text-info" }
        : resolvedEvaluationType === "survey"
          ? { label: "Survey", className: "text-success" }
          : resolvedEvaluationType === "questionnaire"
            ? { label: "Questionnaire", className: "text-warning" }
            : { label: "Evaluation", className: "text-secondary" };

  const fallbackTitle =
    template?.title ||
    evaluations?.[0]?.templateTitle ||
    groupedEvaluations[0]?.evaluations?.[0]?.title ||
    "Template Evaluations";
  const headerTitle = template?.title || fallbackTitle;

  return (
    <div className="bg-light min-vh-100">
      <PageTitle title={`${template?.title || 'Template'} Evaluations`} description="View and manage template evaluations" />
      <Navbar pageTitle={`${template?.title || 'Template'} Evaluations`} />
      
      <Container className="mt-4">
        <Card className="shadow-sm border-0">
          <Card.Header className="border-0 px-4 py-3">
            <div className="d-flex justify-content-between align-items-center">
              <div> 
                <span className={`fw-semibold ${evaluationTypeMeta.className} small`}>
                    {evaluationTypeMeta.label}
                  </span>
                <h6 className="mb-0">
                  {headerTitle}
                </h6>
                <div className="d-flex flex-row align-items-center justify-content-start gap-2">
                  {selectedDueDate && (
                    <div className="text-muted small">
                      <span className="fw-semibold">Due:</span> {new Date(selectedDueDate).toLocaleDateString()}
                    </div>
                  )}
                  <span className="fw-semibold">• </span>
                  <div className="text-muted small">
                      <span className="fw-semibold d-none d-md-inline">Assigned:</span> {groupedEvaluations.length} user{groupedEvaluations.length !== 1 ? 's' : ''}
                  </div>
                
                </div>
              </div>
              <div className="d-flex gap-2">
                {selectedDueDate && (
                  <Button 
                    variant="light-solid" 
                    onClick={() => navigate(`/template-evaluations/${templateId}`)}
                    className="d-flex align-items-center gap-2"
                    size="sm"
                  >
                    <i className="bi bi-calendar"></i>
                    <span className="d-none d-md-block ms-1">View All Dates</span>
                  </Button>
                )}
                {/*<Button 
                  variant="outline-primary" 
                  onClick={() => navigate('/evaluations')}
                  className="d-flex align-items-center gap-2"
                  size="sm"
                >
                  <i className="bi bi-arrow-left"></i>
                  <span className="d-none d-md-block ms-1">Back to Dashboard</span>
                </Button>*/}
              </div>
            </div>
          </Card.Header>
          
          <Card.Body className="p-0">
            {groupedEvaluations.length === 0 ? (
              <div className="text-center p-5">
                <img src="/images/undraw_blank-canvas_a6x5.svg" alt="No evaluations" className="img-fluid" style={{height: '200px'}} />
                <h5 className="mt-3">
                  {selectedDueDate ? 'No Evaluations Found for Selected Date' : 'No Evaluations Found'}
                </h5>
                <p className="text-muted">
                  {selectedDueDate 
                    ? `No evaluations found for ${new Date(selectedDueDate).toLocaleDateString()}.`
                    : 'No evaluations have been created for this template yet.'
                  }
                </p>
                {selectedDueDate && (
                  <Button 
                    variant="primary"
                    onClick={() => navigate(`/template-evaluations/${templateId}`)}
                    className="mt-3"
                  >
                    View All Evaluations
                  </Button>
                )}
              </div>
            ) : (
                             <Table responsive hover className="align-middle">
                 <thead>
                   <tr className="custom-header-bg">
                     <th >Assigned User</th>
                     <th>Evaluation Progress</th>
                     <th>Recurrence Type</th>
                     <th>Actions</th>
                   </tr>
                 </thead>
                 <tbody>
                   {groupedEvaluations.map((group) => {
                     const completedCount = group.evaluations.filter(e => e.status === "completed").length;
                     const pendingCount = group.evaluations.filter(e => e.status !== "completed").length;
                     const totalCount = group.evaluations.length;
                     const completedPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
                     const pendingPercent = totalCount > 0 ? (pendingCount / totalCount) * 100 : 0;
                     const isRecurring = totalCount > 1;

                     return (
                       <tr key={group.residentId}>
                         <td>
                          <div className="d-flex align-items-center gap-2">
  <UserAvatar user={group.resident} size={32} alt="" />{group.residentName}
                          </div>
                         </td>
                         <td style={{ minWidth: "220px" }}>
                           <div className="d-flex flex-wrap gap-3 mb-2 small text-muted fw-semibold">
                             <span>{totalCount} total</span>
                             <span>{completedCount} complete</span>
                             <span>{pendingCount} pending</span>
                           </div>
                           <div className="progress" style={{ height: "6px" }}>
                             <div
                               className="progress-bar bg-success-solid"
                               role="progressbar"
                               style={{ width: `${completedPercent}%` }}
                               aria-valuenow={completedPercent}
                               aria-valuemin="0"
                               aria-valuemax="100"
                             />
                             <div
                               className="progress-bar bg-warning-solid"
                               role="progressbar"
                               style={{ width: `${pendingPercent}%` }}
                               aria-valuenow={pendingPercent}
                               aria-valuemin="0"
                               aria-valuemax="100"
                             />
                           </div>
                         </td>
                         <td>
                           <Badge bg={isRecurring ? "info" : "secondary"}>
                             {isRecurring ? `Recurring (${totalCount} total)` : "Single evaluation"}
                           </Badge>
                         </td>
                         <td>
                           {isRecurring ? (
                             <Button
                               variant="outline-primary"
                               size="sm"
                               onClick={() => {
                                 setSelectedRecurringEvaluation(group.evaluations);
                                 setShowRecurringModal(true);
                               }}
                             >
                               View All Evaluations
                             </Button>
                           ) : (
                             <Button
                               variant="primary"
                               size="sm"
                               onClick={() => {
                                 const evaluation = group.evaluations[0];
                                 if (evaluation.status === "completed") {
                                   navigate(`/evaluations/response/${evaluation.responseId}`);
                                 } else {
                                   navigate(`/evaluations/${evaluation.id}/complete`);
                                 }
                               }}
                               disabled={group.evaluations[0].startTime && new Date(group.evaluations[0].startTime) > new Date()}
                             >
                               {group.evaluations[0].startTime && new Date(group.evaluations[0].startTime) > new Date()
                                 ? `Available ${new Date(group.evaluations[0].startTime).toLocaleDateString()}`
                                 : group.evaluations[0].status === "completed" ? "View" : "Complete"}
                             </Button>
                           )}
                         </td>
                       </tr>
                     );
                   })}
                 </tbody>
               </Table>
            )}
          </Card.Body>
        </Card>
      </Container>

      <RecurringEvaluationsModal
        show={showRecurringModal}
        handleClose={() => {
          setShowRecurringModal(false);
          setSelectedRecurringEvaluation(null);
        }}
        evaluations={selectedRecurringEvaluation}
        residentName={selectedRecurringEvaluation?.[0]?.resident ? 
          `${selectedRecurringEvaluation[0].resident.firstName} ${selectedRecurringEvaluation[0].resident.lastName}` : 
          "Resident"}
      />
    </div>
  );
};

export default TemplateEvaluationsPage; 